// /app/frontend/src/pages/InventoryEnrichment.jsx
// Iter 290zp.13z20 — Layer A Bulk Approval UI.
//
// Purpose: review LLM-suggested structured attrs on every inventory row,
// edit/approve/reject in bulk so the workbench plumbing solver has clean
// data to query against.
//
// Owner/admin gated by the route guard.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PlayCircle, RefreshCw, CheckCircle2, XCircle, Loader2,
  ChevronLeft, ChevronRight, Edit3, Sparkles, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";

const FAMILIES = [
  "elbow", "tee", "reducer", "adapter", "coupling", "union", "nipple",
  "valve", "bend", "end-cap", "plug", "tank-connector", "cap",
  "pipe", "filter", "membrane", "pump", "cable", "breaker",
  "panel", "sensor", "instrument", "bracket", "fastener",
  "chemical", "consumable", "other",
];

const END_TYPES = [
  "compression", "glue-socket", "bsp-male", "bsp-female",
  "npt-male", "npt-female", "push-fit", "john-guest",
  "socket-fusion", "electrofusion", "flange", "barb",
  "quick-connect", "plain", "solvent-weld",
];

const MATERIALS = [
  "pvc-u", "upvc", "cpvc", "hdpe", "pp", "brass",
  "galv-steel", "ss304", "ss316", "abs", "chrome",
  "epdm", "nbr", "polyester", "polycarbonate", "other",
];

const TOP_CATEGORIES = [
  "Pump", "Filter", "Membrane", "Tank", "Fitting", "Valve",
  "Chemical", "Electrical", "Instrument", "Consumable",
  "Bracket / Mount", "Spare Part / Kit", "Hose / Tubing",
  "Resin / Media", "Service", "Other",
];

const PAGE_SIZE = 50;

function StatPill({ label, value, tone = "default", testId }) {
  const toneClasses = {
    default: "bg-white/5 border-white/10 text-white",
    warn: "bg-amber-500/10 border-amber-400/30 text-amber-100",
    good: "bg-emerald-500/10 border-emerald-400/30 text-emerald-100",
    info: "bg-[#1FB6C1]/10 border-[#1FB6C1]/30 text-[#1FB6C1]",
  }[tone];
  return (
    <div
      className={`px-3 py-2 rounded-lg border ${toneClasses}`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="text-lg font-bold">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}

function CellSelect({ value, options, onChange, testId }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full bg-transparent border border-white/10 rounded px-1.5 py-1 text-xs text-white focus:border-[#1FB6C1] outline-none"
      data-testid={testId}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0f1419]">{o}</option>
      ))}
    </select>
  );
}

function CellNumber({ value, onChange, testId }) {
  return (
    <input
      type="number"
      step="any"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className="w-16 bg-transparent border border-white/10 rounded px-1.5 py-1 text-xs text-white focus:border-[#1FB6C1] outline-none"
      data-testid={testId}
    />
  );
}

function ConfBadge({ value }) {
  if (value == null) return <span className="text-[10px] text-white/40">—</span>;
  const v = Number(value);
  const tone = v >= 0.9 ? "bg-emerald-500/20 text-emerald-200"
    : v >= 0.7 ? "bg-amber-500/20 text-amber-200"
    : "bg-rose-500/20 text-rose-200";
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tone}`}>
      {(v * 100).toFixed(0)}%
    </span>
  );
}

export default function InventoryEnrichment() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Filters
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("");
  const [topCategory, setTopCategory] = useState("");
  const [approved, setApproved] = useState(""); // "" | "yes" | "no"
  const [brokenCategory, setBrokenCategory] = useState(false);
  const [minConf, setMinConf] = useState("");

  // Run state
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const pollRef = useRef(null);

  // Cell-level edits buffer (inventory_id -> partial attrs)
  const [edits, setEdits] = useState({});

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/inventory/enrichment/summary");
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load summary.");
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (family) params.set("family", family);
      if (topCategory) params.set("top_category", topCategory);
      if (approved) params.set("approved", approved);
      if (brokenCategory) params.set("broken_category", "true");
      if (minConf) params.set("min_confidence", minConf);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      const { data } = await api.get(`/inventory/enrichment?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load inventory.");
    } finally {
      setLoading(false);
    }
  }, [q, family, topCategory, approved, brokenCategory, minConf, offset]);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await api.get("/inventory/enrichment/runs?limit=10");
      setRuns(data.items || []);
      const running = (data.items || []).find((r) => r.status === "running");
      setActiveRun(running || null);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadSummary(); loadRuns(); }, [loadSummary, loadRuns]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // Poll runs while active
  useEffect(() => {
    if (!activeRun) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => {
      loadRuns();
      loadSummary();
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRun, loadRuns, loadSummary]);

  // Reload items when run completes
  const prevActiveRunRef = useRef(null);
  useEffect(() => {
    if (prevActiveRunRef.current && !activeRun) {
      loadItems();
      toast.success("Enrichment run completed.");
    }
    prevActiveRunRef.current = activeRun;
  }, [activeRun, loadItems]);

  const startRun = async (mode) => {
    try {
      await api.post("/inventory/enrichment/run", { mode });
      toast.success(`Enrichment started (${mode}). Polling progress…`);
      setTimeout(() => loadRuns(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start run.");
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      items.forEach((it) => next.add(it.id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const setEdit = (iid, patch) => {
    setEdits((prev) => ({ ...prev, [iid]: { ...(prev[iid] || {}), ...patch } }));
  };

  const saveCellEdit = async (iid) => {
    const patch = edits[iid];
    if (!patch) return;
    try {
      await api.post(`/inventory/enrichment/patch/${iid}`, patch);
      setEdits((prev) => {
        const n = { ...prev };
        delete n[iid];
        return n;
      });
      await loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const approveSelected = async (bulkUpdates = null) => {
    if (selected.size === 0) {
      toast.error("Select rows first.");
      return;
    }
    try {
      const { data } = await api.post("/inventory/enrichment/approve", {
        ids: Array.from(selected),
        updates: bulkUpdates || undefined,
      });
      toast.success(`Approved ${data.updated} row(s)${data.created ? ` (${data.created} new)` : ""}.`);
      clearSelection();
      await Promise.all([loadItems(), loadSummary()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    }
  };

  const rejectSelected = async () => {
    if (selected.size === 0) {
      toast.error("Select rows first.");
      return;
    }
    try {
      const { data } = await api.post("/inventory/enrichment/reject", {
        ids: Array.from(selected),
      });
      toast.success(`Reverted ${data.rejected} row(s) to pending.`);
      clearSelection();
      await Promise.all([loadItems(), loadSummary()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    }
  };

  const approveHighConfidence = async () => {
    // Auto-select high-confidence pending rows on the current page, then approve.
    const targets = items
      .filter((it) => (it.attrs?.confidence ?? 0) >= 0.9 && !it.attrs?.approved_by)
      .map((it) => it.id);
    if (targets.length === 0) {
      toast.error("No high-confidence pending rows on this page.");
      return;
    }
    try {
      const { data } = await api.post("/inventory/enrichment/approve", {
        ids: targets,
      });
      toast.success(`Approved ${data.updated} high-confidence row(s).`);
      await Promise.all([loadItems(), loadSummary()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed.");
    }
  };

  const pagesTotal = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const runProgress = activeRun
    ? `${activeRun.processed || 0} processed · ${activeRun.ok || 0} ok · ${activeRun.fail || 0} fail`
    : null;

  return (
    <div className="p-6 max-w-[1600px] mx-auto text-white" data-testid="inventory-enrichment-page">
      <header className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <Sparkles className="w-5 h-5 text-[#1FB6C1]" />
          <h1 className="text-2xl font-bold">Inventory Enrichment</h1>
          <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30">
            Layer A · Workbench solver foundation
          </span>
        </div>
        <p className="text-sm text-white/60 max-w-3xl">
          Review LLM-suggested structured attributes on every inventory row.
          Once approved, these attributes power deterministic part matching in
          the Workbench plumbing solver — no more invented SKUs in BoMs.
        </p>
      </header>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5" data-testid="enrichment-summary">
          <StatPill label="Total inventory" value={summary.total_inventory} testId="stat-total" />
          <StatPill label="Enriched" value={`${summary.enriched} / ${summary.total_inventory}`} tone="info" testId="stat-enriched" />
          <StatPill label="Approved" value={summary.approved} tone="good" testId="stat-approved" />
          <StatPill label="Pending" value={summary.pending} tone="warn" testId="stat-pending" />
          <StatPill label="High-conf pending" value={summary.high_conf_pending} tone="info" testId="stat-high-conf-pending" />
        </div>
      )}

      {/* Run controls */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-5" data-testid="enrichment-run-controls">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => startRun("missing_only")}
              disabled={!!activeRun}
              className="px-3 py-2 rounded bg-[#1FB6C1] hover:bg-[#1FB6C1]/80 text-[#0f1419] text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="run-missing-only-btn"
            >
              <PlayCircle className="w-4 h-4" /> Enrich missing rows
            </button>
            <button
              type="button"
              onClick={() => startRun("all")}
              disabled={!!activeRun}
              className="px-3 py-2 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-sm disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="run-all-btn"
              title="Re-enrich every row (human approvals are preserved)"
            >
              <RefreshCw className="w-4 h-4" /> Re-enrich all
            </button>
            <span className="text-xs text-white/40 ml-2">
              Daily incremental cron runs at 02:00 SAST automatically.
            </span>
          </div>
          {activeRun && (
            <div className="flex items-center gap-2 text-sm" data-testid="active-run-banner">
              <Loader2 className="w-4 h-4 animate-spin text-[#1FB6C1]" />
              <span>Run <code className="text-xs">{activeRun.id?.slice(0, 12)}…</code></span>
              <span className="text-white/60">·</span>
              <span className="text-white/80">{runProgress}</span>
            </div>
          )}
        </div>
        {summary?.broken_category_rows > 0 && (
          <div className="mt-3 px-3 py-2 rounded border border-amber-400/40 bg-amber-500/5 text-amber-100 text-xs flex items-start gap-2" data-testid="broken-category-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <b>{summary.broken_category_rows.toLocaleString()} rows</b> have <code>category = "1"</code> (botched import).
              Running enrichment auto-reclassifies them into <code>top_category</code>.
              Use the <em>"Broken category only"</em> filter below to focus on them.
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="enrichment-filters">
        <input
          type="text"
          placeholder="Search name / SKU…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
          className="px-2 py-1.5 bg-transparent border border-white/10 rounded text-sm w-60 focus:border-[#1FB6C1] outline-none"
          data-testid="filter-search"
        />
        <select
          value={family}
          onChange={(e) => { setFamily(e.target.value); setOffset(0); }}
          className="px-2 py-1.5 bg-transparent border border-white/10 rounded text-sm focus:border-[#1FB6C1] outline-none"
          data-testid="filter-family"
        >
          <option value="" className="bg-[#0f1419]">All families</option>
          {FAMILIES.map((f) => <option key={f} value={f} className="bg-[#0f1419]">{f}</option>)}
        </select>
        <select
          value={topCategory}
          onChange={(e) => { setTopCategory(e.target.value); setOffset(0); }}
          className="px-2 py-1.5 bg-transparent border border-white/10 rounded text-sm focus:border-[#1FB6C1] outline-none"
          data-testid="filter-category"
        >
          <option value="" className="bg-[#0f1419]">All categories</option>
          {TOP_CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0f1419]">{c}</option>)}
        </select>
        <select
          value={approved}
          onChange={(e) => { setApproved(e.target.value); setOffset(0); }}
          className="px-2 py-1.5 bg-transparent border border-white/10 rounded text-sm focus:border-[#1FB6C1] outline-none"
          data-testid="filter-approved"
        >
          <option value="" className="bg-[#0f1419]">All</option>
          <option value="yes" className="bg-[#0f1419]">Approved</option>
          <option value="no" className="bg-[#0f1419]">Pending</option>
        </select>
        <select
          value={minConf}
          onChange={(e) => { setMinConf(e.target.value); setOffset(0); }}
          className="px-2 py-1.5 bg-transparent border border-white/10 rounded text-sm focus:border-[#1FB6C1] outline-none"
          data-testid="filter-min-conf"
        >
          <option value="" className="bg-[#0f1419]">Any confidence</option>
          <option value="0.9" className="bg-[#0f1419]">≥ 90%</option>
          <option value="0.7" className="bg-[#0f1419]">≥ 70%</option>
          <option value="0.4" className="bg-[#0f1419]">≥ 40%</option>
        </select>
        <label className="text-xs text-white/70 inline-flex items-center gap-1.5" data-testid="filter-broken-cat-label">
          <input
            type="checkbox"
            checked={brokenCategory}
            onChange={(e) => { setBrokenCategory(e.target.checked); setOffset(0); }}
            data-testid="filter-broken-cat"
          />
          Broken category only
        </label>
        <button
          type="button"
          onClick={() => { setQ(""); setFamily(""); setTopCategory(""); setApproved(""); setBrokenCategory(false); setMinConf(""); setOffset(0); }}
          className="ml-auto text-xs text-white/60 hover:text-white"
          data-testid="filter-clear-btn"
        >
          Clear filters
        </button>
      </div>

      {/* Bulk action bar */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-2 mb-3 flex items-center gap-2 sticky top-0 z-10 backdrop-blur" data-testid="bulk-action-bar">
        <span className="text-xs text-white/70 px-2" data-testid="selected-count">
          {selected.size} selected
        </span>
        <button
          type="button"
          onClick={selectAllOnPage}
          className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
          data-testid="select-page-btn"
        >Select page</button>
        <button
          type="button"
          onClick={clearSelection}
          className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
          data-testid="clear-selection-btn"
        >Clear</button>
        <span className="text-white/20">|</span>
        <button
          type="button"
          onClick={() => approveSelected()}
          disabled={selected.size === 0}
          className="text-xs px-2 py-1 rounded bg-emerald-500/80 hover:bg-emerald-500 text-white disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="approve-selected-btn"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Approve selected
        </button>
        <button
          type="button"
          onClick={rejectSelected}
          disabled={selected.size === 0}
          className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-rose-400/30 text-rose-200 disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="reject-selected-btn"
        >
          <XCircle className="w-3.5 h-3.5" /> Revert to pending
        </button>
        <span className="text-white/20">|</span>
        <button
          type="button"
          onClick={approveHighConfidence}
          className="text-xs px-2 py-1 rounded bg-[#1FB6C1]/80 hover:bg-[#1FB6C1] text-[#0f1419] font-bold inline-flex items-center gap-1"
          data-testid="approve-high-conf-btn"
          title="Auto-approve all pending rows on this page with confidence ≥ 90%"
        >
          ⚡ Approve all ≥90% on page
        </button>
        <span className="ml-auto text-xs text-white/60" data-testid="page-summary">
          {loading ? "Loading…" : `${items.length} of ${total.toLocaleString()} rows`}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto" data-testid="enrichment-table-wrap">
        <table className="w-full text-xs">
          <thead className="bg-white/5 text-left text-[10px] uppercase tracking-wider text-white/60">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="p-2">Name / SKU</th>
              <th className="p-2">Family</th>
              <th className="p-2">Size A</th>
              <th className="p-2">Size B</th>
              <th className="p-2">End A</th>
              <th className="p-2">End B</th>
              <th className="p-2">Material</th>
              <th className="p-2">Top category</th>
              <th className="p-2">Conf</th>
              <th className="p-2 w-24">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.length === 0 && !loading && (
              <tr><td colSpan={11} className="p-8 text-center text-white/40">
                No rows. Try clearing filters or running enrichment.
              </td></tr>
            )}
            {items.map((it) => {
              const attrs = it.attrs || {};
              const edit = edits[it.id] || {};
              const merged = { ...attrs, ...edit };
              const isDirty = Object.keys(edit).length > 0;
              const isApproved = !!attrs.approved_by;
              return (
                <tr key={it.id}
                    className={`hover:bg-white/5 ${isApproved ? "opacity-80" : ""}`}
                    data-testid={`enrichment-row-${it.id}`}
                >
                  <td className="p-2 align-top">
                    <input
                      type="checkbox"
                      checked={selected.has(it.id)}
                      onChange={() => toggleSelected(it.id)}
                      data-testid={`row-checkbox-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top max-w-[280px]">
                    <div className="font-bold text-white" title={it.name}>{it.name}</div>
                    <div className="text-white/50 text-[10px] font-mono">
                      {it.sku || "—"} · {it.category === "1" ? <span className="text-amber-300">cat=1 (broken)</span> : it.category}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <CellSelect
                      value={merged.family}
                      options={FAMILIES}
                      onChange={(v) => setEdit(it.id, { family: v })}
                      testId={`cell-family-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex items-center gap-1">
                      <CellNumber
                        value={merged.size_a}
                        onChange={(v) => setEdit(it.id, { size_a: v })}
                        testId={`cell-size-a-${it.id}`}
                      />
                      <CellSelect
                        value={merged.size_a_unit}
                        options={["mm", "in"]}
                        onChange={(v) => setEdit(it.id, { size_a_unit: v })}
                        testId={`cell-size-a-unit-${it.id}`}
                      />
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex items-center gap-1">
                      <CellNumber
                        value={merged.size_b}
                        onChange={(v) => setEdit(it.id, { size_b: v })}
                        testId={`cell-size-b-${it.id}`}
                      />
                      <CellSelect
                        value={merged.size_b_unit}
                        options={["mm", "in"]}
                        onChange={(v) => setEdit(it.id, { size_b_unit: v })}
                        testId={`cell-size-b-unit-${it.id}`}
                      />
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <CellSelect
                      value={merged.end_a}
                      options={END_TYPES}
                      onChange={(v) => setEdit(it.id, { end_a: v })}
                      testId={`cell-end-a-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <CellSelect
                      value={merged.end_b}
                      options={END_TYPES}
                      onChange={(v) => setEdit(it.id, { end_b: v })}
                      testId={`cell-end-b-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <CellSelect
                      value={merged.material}
                      options={MATERIALS}
                      onChange={(v) => setEdit(it.id, { material: v })}
                      testId={`cell-material-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <CellSelect
                      value={merged.top_category}
                      options={TOP_CATEGORIES}
                      onChange={(v) => setEdit(it.id, { top_category: v })}
                      testId={`cell-top-cat-${it.id}`}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <ConfBadge value={attrs.confidence} />
                  </td>
                  <td className="p-2 align-top">
                    {isDirty ? (
                      <button
                        type="button"
                        onClick={() => saveCellEdit(it.id)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-[#1FB6C1] text-[#0f1419] font-bold inline-flex items-center gap-1"
                        data-testid={`save-row-${it.id}`}
                      >
                        <Edit3 className="w-3 h-3" /> Save
                      </button>
                    ) : isApproved ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 inline-flex items-center gap-1" data-testid={`row-status-approved-${it.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Approved
                      </span>
                    ) : attrs.confidence != null ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid={`row-status-pending-${it.id}`}>Pending</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60" data-testid={`row-status-noattrs-${it.id}`}>No attrs</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 px-2" data-testid="pagination">
        <button
          type="button"
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          disabled={offset === 0 || loading}
          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="prev-page-btn"
        ><ChevronLeft className="w-3.5 h-3.5" /> Prev</button>
        <span className="text-xs text-white/60">
          Page {currentPage} of {pagesTotal.toLocaleString()}
        </span>
        <button
          type="button"
          onClick={() => setOffset(offset + PAGE_SIZE)}
          disabled={offset + PAGE_SIZE >= total || loading}
          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="next-page-btn"
        >Next <ChevronRight className="w-3.5 h-3.5" /></button>
      </div>

      {/* Recent runs panel */}
      {runs.length > 0 && (
        <details className="mt-6 bg-white/5 border border-white/10 rounded-lg p-3" data-testid="recent-runs-panel">
          <summary className="text-xs text-white/70 cursor-pointer">Recent enrichment runs ({runs.length})</summary>
          <table className="w-full text-xs mt-3">
            <thead className="text-[10px] uppercase text-white/50">
              <tr><th className="text-left p-1">Started</th><th>Mode</th><th>Status</th><th>Processed</th><th>OK</th><th>Fail</th><th>By</th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {runs.map((r) => (
                <tr key={r.id} className="hover:bg-white/5">
                  <td className="p-1 text-white/70">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="p-1">{r.mode}</td>
                  <td className="p-1">
                    {r.status === "running" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : r.status}
                  </td>
                  <td className="p-1">{r.processed}</td>
                  <td className="p-1 text-emerald-300">{r.ok}</td>
                  <td className="p-1 text-rose-300">{r.fail}</td>
                  <td className="p-1 text-white/50">{r.started_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
